import { SaveOutlined } from '@ant-design/icons';
import { type StatoValidazione } from '@framework/api-client/api/inquiries-compile/schemas/statoValidazione.ts';
import { useFormStateIsDirty } from '@framework/form/form/useFormStateIsDirty.ts';
import { AsyncButton } from '@framework/ui/components/button/AsyncButton.tsx';
import {
  type ButtonResponsiveProps,
  getResponsiveButtonProps,
} from '@framework/ui/components/button/getResponsiveButtonProps.tsx';
import { type ButtonProps, Tooltip } from 'antd';
import { useMemo } from 'react';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { SubmissionModuleLogic } from '../../../submission/submission-module/SubmissionModuleLogic.ts';
import { useHandleDraftSubmit } from './useHandleDraftSubmit.ts';

export interface IDraftSubmitFormButtonProps extends ButtonProps {
  onValid: (data: any) => Promise<void>;
  validationState?: StatoValidazione | null;
}

/**
 * Come il SubmitFormButton, ma salva il form in bozza.
 * Utilizza useHandleDraftSubmit per creare un processo semplificato
 * di submit del form che, in caso di successo, chiama una funzione
 * passata come prop.
 */
export function DraftSubmitFormButton(props: IDraftSubmitFormButtonProps) {
  const { onValid, validationState, ...otherProps } = props;
  // Destructuring obbligatorio per il corretto funzionamento di useFormState
  const { isSubmitting } = useFormState();
  const { t } = useTranslation();

  const isFormDirty = useFormStateIsDirty();

  // Un modulo è sempre salvabile se non è mai stato visitato
  const isEnabled =
    isFormDirty || !SubmissionModuleLogic.isVisited(validationState);

  const isMobile = useBreakpoint(down('md'));
  const buttonResponsiveProps = useMemo(() => {
    return getResponsiveButtonProps(
      {
        // icon: <SaveOutlined />,
        iconLeftComponent: SaveOutlined,
        iconLeftAriaLabel: t('form.salva', 'Salva'),
        text: t('form.salva', 'Salva'),
        ...otherProps,
      } satisfies ButtonResponsiveProps,
      isMobile,
    );
  }, [t, otherProps, isMobile]);

  const handleSubmit = useHandleDraftSubmit({
    onValid,
  });

  return (
    <Tooltip
      title={
        isEnabled
          ? undefined
          : t('form.non-ci-sono-modifiche', 'Non ci sono modifiche da salvare')
      }
    >
      <AsyncButton
        // icon={<SaveOutlined />}
        type="primary"
        size="large"
        // children={t('front:form.salva', 'Salva')}
        {...buttonResponsiveProps}
        loading={isSubmitting}
        disabled={!isEnabled}
        onClick={handleSubmit}
      />
    </Tooltip>
  );
}
